import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Phone, Home, X, Smartphone, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useActiveFamily } from '../../context/ActiveFamilyContext';
import { useAvailableCallMembers } from '../../hooks/useAvailableCallMembers';
import { useDeviceRegistry } from '../../hooks/useDeviceRegistry';
import { useMemberRole } from '../../hooks/useMemberRole';
import { useFamilyRealtimeStaticData } from '../../context/FamilyRealtimeDataContext';
import { useFamilyMode } from '../../context/FamilyModeContext';
import { usePlanLimits } from '../../hooks/usePlanLimits';
import { useToast } from '../ui/toast/ToastProvider';
import { createCall } from '../../services/callService';
import DeviceConfigModal from './DeviceConfigModal';
import DeviceDetectionPrompt from './DeviceDetectionPrompt';
import Switch from '../form/switch/Switch';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export default function CallModal({ isOpen, onClose }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { activeFamily } = useActiveFamily();
  const { isFeatureBlocked, loading: planLoading } = usePlanLimits();
  const { members, loading } = useAvailableCallMembers(activeFamily?.id || null);
  const {
    isHomeDevice,
    setHomeDeviceEnabled,
    isNative,
    deviceId,
    needsDeviceTypeSelection,
    configureAsHomeDevice,
    configureAsMobileDevice,
    platform,
  } = useDeviceRegistry();
  const { memberId: currentMemberId } = useMemberRole();
  const familyStatic = useFamilyRealtimeStaticData();
  const { enabled: isFamilyMode } = useFamilyMode();
  const { showToast } = useToast();
  const [calling, setCalling] = useState(false);
  const [deviceConfigOpen, setDeviceConfigOpen] = useState(false);

  // Obtener nombre del miembro actual
  const currentMemberName = useMemo(() => {
    if (!currentMemberId) return 'Usuario';
    const member = familyStatic.members.find(m => m.id === currentMemberId);
    return member?.name || 'Usuario';
  }, [currentMemberId, familyStatic.members]);

  const callerDeviceId = deviceId || (!isNative ? `web-${currentMemberId || 'unknown'}` : null);
  const canPlaceCall = Boolean(activeFamily?.id && currentMemberId && callerDeviceId);
  const isCallsBlocked = !planLoading && isFeatureBlocked('familyCalls');
  const canUseCalls = canPlaceCall && !isCallsBlocked;

  if (!isOpen) return null;

  const handleCallHome = async () => {
    if (!activeFamily?.id || !currentMemberId || !callerDeviceId) {
      showToast({ 
        message: t('calls.error.missingData', { defaultValue: 'Datos incompletos para realizar la llamada' }), 
        type: 'error' 
      });
      return;
    }

    if (planLoading) return;
    if (isCallsBlocked) {
      showToast({
        message: t('calls.error.blockedByPlan', { defaultValue: 'Tu plan no incluye llamadas' }),
        type: 'error',
      });
      return;
    }

    setCalling(true);
    try {
      const result = await createCall({
        familyId: activeFamily.id,
        callerMemberId: currentMemberId,
        callerMemberName: currentMemberName || 'Usuario',
        callerDeviceId,
        type: 'home'
      });

      if (result.success) {
        showToast({ 
          message: t('calls.success.callSent', { defaultValue: 'Llamada enviada correctamente' }), 
          type: 'success' 
        });
        onClose();
        navigate(`/calls/outgoing/${result.callId}`);
      } else {
        showToast({ 
          message: result.error || t('calls.error.sendFailed', { defaultValue: 'Error al enviar la llamada' }), 
          type: 'error' 
        });
      }
    } catch (error) {
      console.error('Error al llamar a casa:', error);
      showToast({ 
        message: t('calls.error.unexpected', { defaultValue: 'Error inesperado' }), 
        type: 'error' 
      });
    } finally {
      setCalling(false);
    }
  };

  const handleCallMember = async (memberId: string, memberName: string) => {
    if (!activeFamily?.id || !currentMemberId || !callerDeviceId) {
      showToast({ 
        message: t('calls.error.missingData', { defaultValue: 'Datos incompletos para realizar la llamada' }), 
        type: 'error' 
      });
      return;
    }

    if (planLoading) return;
    if (isCallsBlocked) {
      showToast({
        message: t('calls.error.blockedByPlan', { defaultValue: 'Tu plan no incluye llamadas' }),
        type: 'error',
      });
      return;
    }

    setCalling(true);
    try {
      const result = await createCall({
        familyId: activeFamily.id,
        callerMemberId: currentMemberId,
        callerMemberName: currentMemberName || 'Usuario',
        callerDeviceId,
        type: 'member',
        targetMemberId: memberId,
        targetMemberName: memberName
      });

      if (result.success) {
        showToast({ 
          message: t('calls.success.callSent', { defaultValue: 'Llamada enviada correctamente' }), 
          type: 'success' 
        });
        onClose();
        navigate(`/calls/outgoing/${result.callId}`);
      } else {
        showToast({ 
          message: result.error || t('calls.error.sendFailed', { defaultValue: 'Error al enviar la llamada' }), 
          type: 'error' 
        });
      }
    } catch (error) {
      console.error('Error al llamar a miembro:', error);
      showToast({ 
        message: t('calls.error.unexpected', { defaultValue: 'Error inesperado' }), 
        type: 'error' 
      });
    } finally {
      setCalling(false);
    }
  };

  const availableMembers = members.filter(m => m.hasActiveDevices);

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div 
        className="w-full max-w-md rounded-[24px] shadow-sm max-h-[80vh] overflow-hidden flex flex-col"
        style={{ backgroundColor: '#fff5eb' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-5 rounded-t-[24px]"
          style={{ backgroundColor: '#055590' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Phone className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-black text-white">
              {t('calls.title', { defaultValue: 'Llamar' })}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {/* Botón de configuración - oculto en modo grupo */}
            {!isFamilyMode && (
              <button
                onClick={() => setDeviceConfigOpen(true)}
                className="p-2 rounded-2xl hover:bg-white/20 transition-colors"
                aria-label={t('calls.deviceConfig.title', { defaultValue: 'Configurar Dispositivo' })}
              >
                <Settings className="w-5 h-5 text-white" />
              </button>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-2xl hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Home Device Toggle (solo en dispositivos nativos) */}
          {isNative && (
            <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-3">
                <Smartphone className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <Switch
                    label={t('calls.homeDevice.label', { defaultValue: 'Dispositivo de Casa' })}
                    checked={isHomeDevice}
                    onChange={(next) => setHomeDeviceEnabled(next)}
                  />
                  <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                    {t('calls.homeDevice.description', { defaultValue: 'Marca este dispositivo como el teléfono de casa para recibir llamadas familiares' })}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Call Home Button */}
          <button
            onClick={handleCallHome}
            disabled={calling || !canUseCalls}
            className="w-full p-4 rounded-2xl font-black shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            style={{ backgroundColor: '#ef6e6b', color: '#ffffff' }}
          >
            <Home className="w-5 h-5" />
            {calling ? t('calls.calling', { defaultValue: 'Llamando...' }) : t('calls.callHome', { defaultValue: 'Llamar a Casa' })}
          </button>

          {/* Members List */}
          {loading ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
              {t('common.loading', { defaultValue: 'Cargando...' })}
            </div>
          ) : availableMembers.length > 0 ? (
            <div className="space-y-2">
              <h3 className="text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider px-1">
                {t('calls.callMember', { defaultValue: 'Llamar a un miembro' })}
              </h3>
              {availableMembers.map(member => (
                <button
                  key={member.memberId}
                  onClick={() => handleCallMember(member.memberId, member.memberName)}
                  disabled={calling || !canUseCalls}
                  className="w-full p-4 rounded-2xl transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3"
                  style={{ backgroundColor: '#ffffff', opacity: 0.8 }}
                >
                  <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-white font-black text-sm flex-shrink-0"
                    style={{ backgroundColor: '#055590' }}
                  >
                    {member.memberName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className="font-black text-gray-900 dark:text-white truncate">
                      {member.memberName}
                    </p>
                    <p className="text-xs text-green-600 dark:text-green-400">
                      {t('calls.available', { defaultValue: 'Disponible' })}
                    </p>
                  </div>
                  <Phone className="w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-3">
                <Phone className="w-8 h-8 text-gray-400 dark:text-gray-600" />
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('calls.noMembersAvailable', { defaultValue: 'No hay miembros con dispositivos activos' })}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de configuración de dispositivo */}
      <DeviceConfigModal
        isOpen={deviceConfigOpen}
        onClose={() => setDeviceConfigOpen(false)}
        onConfigured={() => {
          setDeviceConfigOpen(false);
          showToast({ 
            message: t('common.savedSuccessfully', { defaultValue: 'Guardado correctamente' }), 
            type: 'success' 
          });
        }}
      />

      {/* Prompt de detección automática de nuevo dispositivo */}
      <DeviceDetectionPrompt
        isOpen={needsDeviceTypeSelection}
        onClose={() => {}} // No permitir cerrar sin seleccionar
        onSetAsHome={configureAsHomeDevice}
        onSetAsMobile={configureAsMobileDevice}
        devicePlatform={platform}
      />
    </div>
  );
}
